import { createSlice, createAsyncThunk, createEntityAdapter } from "@reduxjs/toolkit";
import busytimeApiController from "../../services/busytime.service";
import HandleError from "../HandleError";
import HandleResponse from "../HandleResponse";
import { contentErrorMessage } from "./contentError";
import { persistedBusytimeId, busytimeSalonContext, busytimeLoadMessage } from "../../services/busytimeIdentity";
import { getFrameWindow, mergeCalendarFrame } from "../../pages/calendar/calendarMutationRefresh";
export const usersAdapter = createEntityAdapter();

const busytimeErrorMessage = (action, fallback) => action.payload?.userMessage || contentErrorMessage(action, fallback);

export const busytimeStoreApi = createAsyncThunk("busytime/create", async (formvalues, thunkAPI) => {
  try {
    const resposedata = await busytimeApiController
      .create(formvalues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "create"))
      .catch((error) => HandleError(thunkAPI, error, "create"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue({ status: error.response.status, message: message });
  }
});

export const busytimeUpdateApi = createAsyncThunk("busytime/update", async (formvalues, thunkAPI) => {
  try {
    const resposedata = await busytimeApiController
      .update(formvalues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "update"))
      .catch((error) => HandleError(thunkAPI, error, "update"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue({ status: error.response.status, message: message });
  }
});

export const busytimeListViewApi = createAsyncThunk("busytime/listview", async (formValues, thunkAPI) => {
  try {
    const resposedata = await busytimeApiController
      .view(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "listview"))
      .catch((error) => HandleError(thunkAPI, error, "listview"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

/**
 * Refresh only the busy-time frame a save changed, merging the response into
 * the calendar data already on screen.
 */
export const busytimeFrameRefreshApi = createAsyncThunk("busytime/framerefresh", async (formValues, thunkAPI) => {
  try {
    const resposedata = await busytimeApiController
      .view(formValues?.range || {}, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "listview"))
      .catch((error) => HandleError(thunkAPI, error, "listview"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const busytimeOptions = createAsyncThunk("busytime/busytimeOptions", async (formValues, thunkAPI) => {
  try {
    const resposedata = await busytimeApiController
      .view(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "busytimeOptions"))
      .catch((error) => HandleError(thunkAPI, error, "busytimeOptions"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const busytimeDetailApi = createAsyncThunk("busytime/detail", async (formValues, thunkAPI) => {
  const id = persistedBusytimeId(formValues?.id);
  if (id === null) return thunkAPI.rejectWithValue({userMessage: busytimeLoadMessage});
  const salon = busytimeSalonContext(thunkAPI.getState().auth?.user);
  try {
    const response = await busytimeApiController.view({...formValues, id}, thunkAPI);
    if (salon !== busytimeSalonContext(thunkAPI.getState().auth?.user)
        || thunkAPI.getState().busytime.activeDetailRequestId !== thunkAPI.requestId) {
      return thunkAPI.rejectWithValue({userMessage: 'Busy time selection changed. Select it again.'});
    }
    if (response?.status < 200 || response?.status >= 300) return HandleResponse(thunkAPI, response);
    if (persistedBusytimeId(response?.data?.id) !== id) {
      return thunkAPI.rejectWithValue({userMessage: busytimeLoadMessage});
    }
    return thunkAPI.fulfillWithValue(response.data, {salon});
  } catch (error) {
    return HandleError(thunkAPI, error, "detail");
  }
});

export const busytimeDeleteApi = createAsyncThunk("busytime/delete", async (formValues, thunkAPI) => {
  try {
    const resposedata = await busytimeApiController
      .deleted(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "delete"))
      .catch((error) => HandleError(thunkAPI, error, "delete"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const busytimeSuggetionListApi = createAsyncThunk("busytime/suggetionlist", async (formValues, thunkAPI) => {
  try {
    const resposedata = await busytimeApiController
      .suggetionlist(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "suggetionlist"))
      .catch((error) => HandleError(thunkAPI, error, "suggetionlist"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

const initialState = {
  isOpenedAddForm: "",
  isOpenedEditForm: "",
  isListView: [],
  isListViewError: "",
  isDetailData: "",
  isDetailError: "",
  activeDetailRequestId: null,
  detailRequestedId: null,
  detailSalon: null,
  isApiStatus: {
    busytimeStoreApi: "",
    busytimeUpdateApi: "",
    busytimeListViewApi: "",
    busytimeFrameRefreshApi: "",
    busytimeDetailApi: "",
    busytimeDeleteApi: "",
  },
};

const busytimeSlice = createSlice({
  name: "busytime",
  initialState,
  reducers: {
    reset: () => initialState,
    AddBusytimeForm: (state, action) => {
      state.activeDetailRequestId = null;
      state.isDetailData = "";
      state.isOpenedEditForm = "";
      state.isOpenedAddForm = action.payload;
    },
    EditBusytimeForm: (state, action) => {
      if (!action.payload) {
        state.activeDetailRequestId = null;
        state.isDetailData = "";
      }
      state.isOpenedAddForm = "";
      state.isOpenedEditForm = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(busytimeStoreApi.pending, () => { })
      .addCase(busytimeStoreApi.fulfilled, () => { })
      .addCase(busytimeStoreApi.rejected, () => { })
      .addCase(busytimeUpdateApi.pending, () => { })
      .addCase(busytimeUpdateApi.fulfilled, () => { })
      .addCase(busytimeUpdateApi.rejected, () => { })
      .addCase(busytimeFrameRefreshApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, busytimeFrameRefreshApi: "loading" };
      })
      .addCase(busytimeFrameRefreshApi.fulfilled, (state, action) => {
        const payload = action.payload;
        const window = getFrameWindow(action.meta && action.meta.arg ? action.meta.arg.appointments : null);
        if (Array.isArray(payload)) {
          state.isListView = mergeCalendarFrame(state.isListView, payload, window);
        } else if (payload && Array.isArray(payload.data)) {
          const current = Array.isArray(state.isListView) ? state.isListView : state.isListView?.data;
          state.isListView = { ...payload, data: mergeCalendarFrame(current, payload.data, window) };
        } else {
          state.isListView = payload;
        }
        state.isApiStatus = { ...state.isApiStatus, busytimeFrameRefreshApi: "succeeded" };
      })
      .addCase(busytimeFrameRefreshApi.rejected, (state) => {
        state.isApiStatus = { ...state.isApiStatus, busytimeFrameRefreshApi: "failed" };
      })
      .addCase(busytimeListViewApi.pending, (state) => {
        state.isListViewError = "";
        state.isApiStatus = { ...state.isApiStatus, busytimeListViewApi: "loading" };
      })
      .addCase(busytimeListViewApi.fulfilled, (state, action) => {
        let old_current_page = state.isListView.current_page ? state.isListView.current_page : "";
        let new_current_page = action.payload.current_page ? action.payload.current_page : "";
        let viewdata = state.isListView && state.isListView.data;
        let newviewdata = action.payload && action.payload.data;
        state.isListView = action.payload;
        if (old_current_page && new_current_page && old_current_page < new_current_page && old_current_page != new_current_page) {
          viewdata && newviewdata ? (state.isListView.data = [...viewdata, ...newviewdata]) : action.payload;
        }
        state.isListView = action.payload;
        state.isListViewError = "";
        state.isApiStatus = { ...state.isApiStatus, busytimeListViewApi: "succeeded" };
      })
      .addCase(busytimeListViewApi.rejected, (state, action) => {
        state.isApiStatus = { ...state.isApiStatus, busytimeListViewApi: "failed" };
        state.isListViewError = busytimeErrorMessage(action, "Unable to load busy times.");
      })
      .addCase(busytimeDetailApi.pending, (state, action) => {
        state.activeDetailRequestId = action.meta.requestId;
        state.detailRequestedId = persistedBusytimeId(action.meta.arg?.id);
        state.isDetailData = "";
        state.isDetailError = "";
        state.detailSalon = null;
        state.isApiStatus = { ...state.isApiStatus, busytimeDetailApi: "loading" };
      })
      .addCase(busytimeDetailApi.fulfilled, (state, action) => {
        if (state.activeDetailRequestId !== action.meta.requestId) return;
        state.isDetailData = action.payload;
        state.detailSalon = action.meta.salon ?? null;
        state.isApiStatus = { ...state.isApiStatus, busytimeDetailApi: "succeeded" };
      })
      .addCase(busytimeDetailApi.rejected, (state, action) => {
        if (state.activeDetailRequestId !== action.meta.requestId) return;
        state.isDetailData = "";
        state.isDetailError = busytimeErrorMessage(action, busytimeLoadMessage);
        state.isApiStatus = { ...state.isApiStatus, busytimeDetailApi: "failed" };
      })
      .addCase(busytimeDeleteApi.pending, () => { })
      .addCase(busytimeDeleteApi.fulfilled, () => { })
      .addCase(busytimeDeleteApi.rejected, () => { });
  },
});
// Action creators are generated for each case reducer function
export const { reset, AddBusytimeForm, EditBusytimeForm } = busytimeSlice.actions;
export default busytimeSlice.reducer;
